import { z } from "zod";

/** Reusable UUID identifier schema for note-scoped inputs (get, delete, restore). */
export const idSchema = z.object({
  id: z.string().uuid("Invalid note ID"),
});

/** Schema for creating a new note. Requires title and content. */
export const createNoteSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title must be 255 characters or less"),
  content: z
    .string()
    .min(1, "Content is required")
    .max(10000, "Content must be 10,000 characters or less"),
});

/** Schema for updating an existing note. Requires note ID and at least one field to update. */
export const updateNoteSchema = z
  .object({
    id: z.string().uuid("Invalid note ID"),
    title: z
      .string()
      .min(1, "Title is required")
      .max(255, "Title must be 255 characters or less")
      .optional(),
    content: z
      .string()
      .min(1, "Content is required")
      .max(10000, "Content must be 10,000 characters or less")
      .optional(),
  })
  .refine((data) => data.title !== undefined || data.content !== undefined, {
    message: "At least one field (title or content) must be provided",
  });

/**
 * Schema for the edit note form. Extends createNoteSchema with a required ID.
 * Unlike updateNoteSchema (which allows partial updates via the API), the edit form
 * always sends both title and content since they are pre-filled.
 */
export const editNoteSchema = createNoteSchema.extend({
  id: z.string().uuid("Invalid note ID"),
});

/** Schema for deleting a note (soft delete). Requires note ID. */
export const deleteNoteSchema = idSchema;

/** Schema for restoring a soft-deleted note. Requires note ID. */
export const restoreNoteSchema = idSchema;

/** Inferred type for create note input values. */
export type CreateNoteInput = z.infer<typeof createNoteSchema>;

/** Inferred type for edit note form values (create fields + id). */
export type EditNoteInput = z.infer<typeof editNoteSchema>;

/** Inferred type for update note input values (API-level, partial fields). */
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;

/** Inferred type for delete note input values. */
export type DeleteNoteInput = z.infer<typeof deleteNoteSchema>;

/** Inferred type for restore note input values. */
export type RestoreNoteInput = z.infer<typeof restoreNoteSchema>;
